
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface BookingCoupon {
  id: string;
  booking_id: string;
  user_coupon_id: string;
  discount_amount: number;
  created_at: string;
  user_coupon: {
    id: string;
    coupon: {
      id: string;
      code: string;
      name: string;
      discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
      discount_value: number;
    };
  };
}

export interface AvailableCoupon {
  id: string;
  user_id: string;
  coupon_id: string;
  provider_id: string;
  is_active: boolean;
  times_used: number;
  applied_at: string;
  created_at: string;
  updated_at: string;
  coupon: {
    id: string;
    code: string;
    name: string;
    description?: string;
    discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
    discount_value: number;
    credits_per_period?: number;
    is_active: boolean;
    valid_from: string;
    valid_until?: string;
    provider_id: string;
    coupon_services: { service_id: string }[];
  };
}

export const useBookingCoupons = (bookingId?: string) => {
  return useQuery({
    queryKey: ['booking-coupons', bookingId],
    queryFn: async () => {
      if (!bookingId) return [];

      const { data, error } = await supabase
        .from('booking_coupons')
        .select(`
          *,
          user_coupon:user_coupons(
            id,
            coupon:coupons(
              id,
              code,
              name,
              discount_type,
              discount_value
            )
          )
        `)
        .eq('booking_id', bookingId);

      if (error) {
        console.error('Error fetching booking coupons:', error);
        throw error;
      }

      return data as BookingCoupon[];
    },
    enabled: !!bookingId,
  });
};

export const useApplyCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ bookingId, userCouponId, discountAmount }: {
      bookingId: string;
      userCouponId: string;
      discountAmount: number;
    }) => {
      const { data, error } = await supabase
        .from('booking_coupons')
        .insert({
          booking_id: bookingId,
          user_coupon_id: userCouponId,
          discount_amount: discountAmount
        })
        .select()
        .single();

      if (error) {
        console.error('Error applying coupon:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Coupon Applied",
        description: "Your discount has been applied to the booking.",
      });
      
      queryClient.invalidateQueries({ queryKey: ['booking-coupons'] });
    },
    onError: (error: any) => {
      console.error('Error applying coupon:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to apply coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useAvailableBookingCoupons = (serviceId?: string, providerId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['available-booking-coupons', serviceId, providerId, user?.id],
    queryFn: async () => {
      if (!user || !serviceId || !providerId) return [];

      // Get user's active coupons for this provider
      const { data: userCoupons, error } = await supabase
        .from('user_coupons')
        .select(`
          *,
          coupon:coupons(
            id,
            code,
            name,
            description,
            discount_type,
            discount_value,
            credits_per_period,
            is_active,
            valid_from,
            valid_until,
            provider_id,
            coupon_services:coupon_services(service_id)
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('provider_id', providerId);

      if (error) {
        console.error('Error fetching user coupons:', error);
        throw error;
      }

      // Filter coupons that are valid and apply to this service
      const now = new Date();
      const validCoupons = userCoupons?.filter(userCoupon => {
        const coupon = userCoupon.coupon;
        if (!coupon || !coupon.is_active) return false;

        // Check date validity
        const validFrom = coupon.valid_from ? new Date(coupon.valid_from) : null;
        const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;
        
        if (validFrom && now < validFrom) return false;
        if (validUntil && now > validUntil) return false;

        // Check if coupon applies to this service (if no specific services, applies to all)
        const couponServices = coupon.coupon_services || [];
        if (couponServices.length === 0) return true; // Applies to all services
        
        return couponServices.some(cs => cs.service_id === serviceId);
      }) || [];

      return validCoupons as AvailableCoupon[];
    },
    enabled: !!user && !!serviceId && !!providerId,
  });
};
